// src/components/AIInvoiceExtractor.tsx
// Step 8 — AI-powered invoice text extraction via Anthropic Claude API
// Genuinely functional: paste invoice text → Claude extracts structured data → review → import

import { useState } from 'react'
import { Sparkles, Loader, CheckCircle, AlertTriangle, X, RefreshCw, Info } from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────

interface ExtractedLineItem {
  description: string
  quantity: number
  unit: string
  unitPriceExGST: number
  totalExGST: number
}

interface ExtractedInvoice {
  supplierName: string
  supplierABN: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  lineItems: ExtractedLineItem[]
  subtotalExGST: number
  gst: number
  totalIncGST: number
  notes: string
  confidence: number
}

// ── Formatters ─────────────────────────────────────────────────────

const fmtAUD = (n: number) => new Intl.NumberFormat('en-AU', { style:'currency', currency:'AUD', minimumFractionDigits:2 }).format(n)

// ── Sample invoice texts for quick-fill ───────────────────────────

const SAMPLE_INVOICES = [
  {
    label: 'Boral concrete invoice',
    text: `BORAL CONSTRUCTION MATERIALS GROUP LIMITED
ABN 12 004 796 249
Tax Invoice
Invoice No: BCQ-INV-2026-0512
Date: 24 June 2026
Payment due: 24 July 2026

Bill to: MCB Civil & Electrical Pty Ltd

ITEM                                          QTY    UNIT   UNIT PRICE   TOTAL
Ready-mix concrete Grade 40 20mm aggregate    1.5    m3     248.00       372.00
Concrete pump hire - 2hr minimum              1      hour   280.00       280.00

Subtotal ex GST                                                           652.00
GST 10%                                                                    65.20
TOTAL AMOUNT DUE                                                          717.20`,
  },
  {
    label: 'Saferoads materials',
    text: `SAFEROADS QUEENSLAND PTY LTD  ABN 89 124 603 271
TAX INVOICE  No: SRD-INV-2026-0601  Date: 22/06/2026  Due: 22/07/2026

Customer: MCB Civil & Electrical

DIN base plate Type F-2 (galvanised)    4ea    @ $420.00    = $1,680.00
UPVC 100mm conduit 6m lengths           30ea   @ $38.40     = $1,152.00
Cable pit 300x450 trafficable lid       3ea    @ $245.00    = $735.00

Sub-total (ex GST): $3,567.00
GST: $356.70
TOTAL: $3,923.70`,
  },
]

// ── AI extraction call ────────────────────────────────────────────

async function extractInvoiceWithAI(text: string): Promise<ExtractedInvoice> {
  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: 1000,
      messages: [{
        role: 'user',
        content: `You are an invoice extraction system for an Australian civil and electrical contractor. Extract all fields from this invoice text and return ONLY valid JSON with no markdown or code fences.

Required JSON structure:
{
  "supplierName": "",
  "supplierABN": "",
  "invoiceNumber": "",
  "invoiceDate": "YYYY-MM-DD",
  "dueDate": "YYYY-MM-DD",
  "lineItems": [
    {
      "description": "",
      "quantity": 0,
      "unit": "",
      "unitPriceExGST": 0,
      "totalExGST": 0
    }
  ],
  "subtotalExGST": 0,
  "gst": 0,
  "totalIncGST": 0,
  "notes": "",
  "confidence": 0.95
}

Rules:
- All prices must be numbers (no $ symbols)
- Dates must be YYYY-MM-DD format
- confidence is 0.0–1.0 based on how clearly the data was present
- If a field cannot be found, use empty string "" for strings and 0 for numbers
- ABN format: XX XXX XXX XXX or XXXXXXXXXXX (strip spaces in output)

Invoice text:
${text}`
      }]
    })
  })

  const data = await resp.json()
  const raw = (data.content?.[0]?.text ?? '').trim()
  // Strip any accidental markdown fences
  const clean = raw.replace(/^```[a-z]*\n?/, '').replace(/\n?```$/, '').trim()
  return JSON.parse(clean) as ExtractedInvoice
}

// ── Component ─────────────────────────────────────────────────────

interface AIInvoiceExtractorProps {
  onImport: (invoice: ExtractedInvoice) => void
  onClose: () => void
}

export function AIInvoiceExtractor({ onImport, onClose }: AIInvoiceExtractorProps) {
  const [invoiceText, setInvoiceText] = useState('')
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState<string | null>(null)
  const [result, setResult]           = useState<ExtractedInvoice | null>(null)
  const [editing, setEditing]         = useState<Partial<ExtractedInvoice>>({})

  const merged = result ? { ...result, ...editing } : null

  const handleExtract = async () => {
    if (!invoiceText.trim()) return
    setLoading(true)
    setError(null)
    setResult(null)
    setEditing({})
    try {
      const extracted = await extractInvoiceWithAI(invoiceText)
      setResult(extracted)
    } catch (e) {
      setError('Claude could not parse the invoice. Check the text is legible and try again, or use the manual import form.')
    } finally {
      setLoading(false)
    }
  }

  const handleImport = () => {
    if (!merged) return
    onImport(merged)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-blue-50 rounded-t-2xl">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />AI Invoice Extraction
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Powered by Claude · paste invoice text and AI extracts all fields</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">

          {/* Input section */}
          {!result && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Paste invoice text</label>
                  <div className="flex gap-1.5">
                    {SAMPLE_INVOICES.map(s => (
                      <button key={s.label} onClick={() => setInvoiceText(s.text)}
                        className="text-xs text-[#2E75B6] hover:underline px-2 py-0.5 bg-blue-50 rounded">
                        Try: {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  className="input w-full text-xs font-mono resize-none leading-relaxed"
                  rows={12}
                  placeholder={`Paste the raw text from a supplier invoice here.

Works with:
  • Text copied directly from a PDF
  • Typed invoice details  
  • Data exported from supplier systems

Claude will extract: supplier name, ABN, invoice number, dates, all line items with quantities and prices, GST, and totals.`}
                  value={invoiceText}
                  onChange={e => setInvoiceText(e.target.value)}
                />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-800 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{error}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
                <button onClick={handleExtract} disabled={!invoiceText.trim() || loading}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50 bg-purple-600 hover:bg-purple-700">
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {loading ? 'Claude is extracting…' : 'Extract with AI'}
                </button>
              </div>
            </>
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center py-8 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-purple-500 animate-pulse" />
              </div>
              <div className="text-sm font-medium text-gray-700">Claude is reading the invoice…</div>
              <div className="text-xs text-gray-400">Extracting supplier, line items, amounts, and dates</div>
            </div>
          )}

          {/* Results */}
          {result && merged && (
            <>
              {/* Confidence indicator */}
              <div className={`rounded-xl border px-3 py-2.5 flex items-center gap-3 ${
                result.confidence >= 0.9 ? 'bg-green-50 border-green-200' :
                result.confidence >= 0.7 ? 'bg-amber-50 border-amber-200' :
                'bg-red-50 border-red-200'
              }`}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  result.confidence >= 0.9 ? 'bg-green-500' : result.confidence >= 0.7 ? 'bg-amber-500' : 'bg-red-500'
                }`} />
                <div className="flex-1 text-xs text-gray-700">
                  <strong>AI confidence: {Math.round(result.confidence * 100)}%</strong> — {
                    result.confidence >= 0.9 ? 'High confidence extraction. Review and import.'
                    : result.confidence >= 0.7 ? 'Good extraction. Review the fields below and correct any errors.'
                    : 'Low confidence — please review all fields carefully before importing.'
                  }
                </div>
                <button onClick={() => { setResult(null); setEditing({}) }} className="text-xs text-gray-500 hover:underline flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" />Re-extract
                </button>
              </div>

              {/* Extracted header data */}
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Invoice details</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label:'Supplier', key:'supplierName', value:merged.supplierName },
                    { label:'ABN', key:'supplierABN', value:merged.supplierABN },
                    { label:'Invoice number', key:'invoiceNumber', value:merged.invoiceNumber },
                    { label:'Invoice date', key:'invoiceDate', value:merged.invoiceDate },
                    { label:'Due date', key:'dueDate', value:merged.dueDate },
                  ].map(({ label, key, value }) => (
                    <div key={key} className={`rounded-xl border px-3 py-2 ${!value ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                      <label className="block text-[10px] text-gray-400 mb-0.5">{label}</label>
                      <input className="w-full text-xs font-medium text-gray-900 bg-transparent outline-none"
                        defaultValue={value}
                        onChange={e => setEditing(prev => ({ ...prev, [key]: e.target.value }))} />
                      {!value && <div className="text-[10px] text-amber-600 mt-0.5">Not found — enter manually</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Line items */}
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Line items</div>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Description</th>
                        <th className="text-right px-2 py-2 font-medium text-gray-600 w-14">Qty</th>
                        <th className="text-left px-2 py-2 font-medium text-gray-600 w-12">Unit</th>
                        <th className="text-right px-2 py-2 font-medium text-gray-600 w-22">Unit price</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-600 w-24">Total ex GST</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {merged.lineItems.map((item, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-800">{item.description}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{item.quantity}</td>
                          <td className="px-2 py-2 text-gray-500">{item.unit}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{fmtAUD(item.unitPriceExGST)}</td>
                          <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtAUD(item.totalExGST)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t text-xs">
                      <tr><td colSpan={4} className="px-3 py-1.5 text-gray-500">Subtotal ex GST</td><td className="px-3 py-1.5 text-right font-semibold tabular-nums">{fmtAUD(merged.subtotalExGST)}</td></tr>
                      <tr><td colSpan={4} className="px-3 py-1 text-gray-400">GST (10%)</td><td className="px-3 py-1 text-right text-gray-500 tabular-nums">{fmtAUD(merged.gst)}</td></tr>
                      <tr><td colSpan={4} className="px-3 py-2 font-bold text-gray-900">Total inc GST</td><td className="px-3 py-2 text-right font-bold text-gray-900 tabular-nums">{fmtAUD(merged.totalIncGST)}</td></tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setResult(null); setEditing({}) }} className="btn-secondary text-sm flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" />Re-extract
                </button>
                <button onClick={handleImport} className="btn-primary text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />Import this invoice
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
